import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { LogglyService } from 'ngx-loggly-logger';

import { EnvironmentService } from '../environment.service';
import { UserSessionService } from '../user-session.service';

import { SG_LOG_LEVELS, BaseErrorLog, ErrorEventLog, BaseLog, HttpErrorLog } from './log-interface';

export interface SgLogRecord {
  level?: SG_LOG_LEVELS;
  message: string;
}

export interface SgHttpErrorLogRecord {
  error: HttpErrorResponse;
  req?: HttpRequest<any>;
  time?: { start: number; end: number };
  correlationId?: string;
}

@Injectable()
export class SgLogglyTracker extends LogglyService {
  /** A list of http headers to post in the logs */
  private httpHeaders: string[];
  private consoleEventHandler: (errorEvent: ErrorEvent) => void;

  constructor(
    http: HttpClient,
    private env: EnvironmentService,
    private userSession: UserSessionService
  ) {
    super(http);

    this.consoleEventHandler = (errorEvent: ErrorEvent) => this.eventError(errorEvent);

    super.push({
      logglyKey: env.loggly.key,
      sendConsoleErrors: true,
      tag: env.loggly.tag || env.env,
    });
  }

  setSendConsoleError(tracker: any, sendConsoleErrors: any) {
    if (window && tracker.sendConsoleErrors !== sendConsoleErrors) {
      if (sendConsoleErrors) {
        window.addEventListener('error', this.consoleEventHandler);
      } else {
        window.removeEventListener('error', this.consoleEventHandler);
      }
      tracker.sendConsoleErrors = sendConsoleErrors;
    }
  }

  push(message: string): void {
    this.sendLog(this.createBaseLog({ message }));
  }

  info(message: string) {
    this.push(message);
  }

  warn(message: string): void {
    this.sendLog(this.createBaseLog({ message, level: 'WARN' }));
  }

  error(error: Error, stack?: boolean): void;
  error(error: string): void;
  error(error: string | Error, stack = true): void {
    const baseLog =
      typeof error === 'string' ? this.parseError(new Error(error), false) : this.parseError(error, stack);
    this.sendLog(baseLog);
  }

  customError<T extends BaseErrorLog = BaseErrorLog>(error: Error, beforeSend: (errorLog: T) => void): void {
    const baseLog: T = this.parseError(error);
    beforeSend(baseLog);
    this.sendLog(baseLog);
  }

  websocketError(error: string | Error, stack = true): void {
    let baseLog: BaseErrorLog;

    if (typeof error === 'string') {
      baseLog = this.parseError(new Error(error), false);
    } else if (error instanceof HttpErrorResponse) {
      baseLog = this.createHttpError({ error });
    } else {
      baseLog = this.parseError(error, stack);
    }
    baseLog.errorSource = 'websocket';
    this.sendLog(baseLog);
  }

  eventError(errorEvent: ErrorEvent, beforeSend?: (errorLog: BaseErrorLog) => void): void {
    const log = this.toEventError(this.createBaseLog({ message: '', level: 'ERROR' }), errorEvent);
    beforeSend && beforeSend(log);
    this.sendLog(log);
  }

  httpError(errorInfo: SgHttpErrorLogRecord): void {
    const error = errorInfo.error;

    if (error.error instanceof ErrorEvent) {
      this.sendLog(this.toEventError(this.parseError(error), error.error));
    } else {
      if (error.status !== 404) {
        this.sendLog(this.createHttpError(errorInfo));
      }
    }
  }

  /**
   * Set the list of http headers to log for each http error
   */
  setHttpHeaders(headers: string[]): void {
    this.httpHeaders = headers.slice();
  }

  private createHttpError(errorInfo: SgHttpErrorLogRecord): HttpErrorLog {
    const error = errorInfo.error;
    const log: HttpErrorLog = <any>this.parseError(error);

    log.errorSource = 'httpError';
    log.message = error.message;

    // Flat top-level keys — Loggly's indexed view keeps primitives cleanly;
    // nested objects get silently dropped from the UI.
    log.httpStatus = error.status;
    if (error.statusText) log.httpStatusText = error.statusText;
    if (error.error !== undefined && error.error !== null) {
      // Response body can be object, array, or string — serialise for indexing.
      log.httpBody = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
    }
    if (errorInfo.req) {
      log.httpMethod = errorInfo.req.method;
      log.httpUrl = errorInfo.req.urlWithParams;
    }
    if (errorInfo.time) {
      log.httpTimeStart = new Date(errorInfo.time.start).toISOString();
      log.httpElapsedMs = errorInfo.time.end - errorInfo.time.start;
    }
    if (errorInfo.correlationId) log.correlationId = errorInfo.correlationId;

    if (this.httpHeaders && error.headers) {
      for (const h of this.httpHeaders) {
        if (error.headers.has(h)) {
          log[`httpHeader_${h.replace(/-/g, '_')}`] = error.headers.get(h);
        }
      }
    }

    delete log.userAgent;
    return log;
  }

  private toEventError(log: BaseLog, errorEvent: ErrorEvent): ErrorEventLog {
    const result: ErrorEventLog = <any>Object.assign({}, log);
    result.errorSource = 'errorEvent';
    result.message = errorEvent.message;
    result.errorEvent = {
      colno: errorEvent.colno,
      filename: errorEvent.filename,
      lineno: errorEvent.lineno,
    };
    return result;
  }

  private parseError<T extends BaseErrorLog = BaseErrorLog>(error: Error, stack = true): T {
    const message: string = describeError(error);
    const log: BaseErrorLog = {
      ...this.createBaseLog({ level: 'ERROR', message }),
      errorSource: 'generic',
      url: location.pathname + location.search + location.hash,
      userId: this.userSession.userProfile.id,
    };

    if (stack && (<Error>error)?.stack) {
      log.stack = (<Error>error).stack;
    }

    const name = this.userSession.userProfile.name;
    if (name) {
      log.userName = name;
    }

    log.userAgent = navigator.userAgent;

    return log as T;
  }

  private createBaseLog(rec: SgLogRecord): BaseLog {
    return {
      ApplicationId: this.env.appName,
      level: rec.level || 'INFO',
      message: rec.message,
    };
  }

  private sendLog(log: BaseLog): void {
    super.track(log).subscribe(
      () => {},
      (error) => {
        console.log(error);
      }
    );
  }
}

/** Keys whose values are never sent to loggly when a thrown object is serialised. */
const SENSITIVE_KEY = /token|password|passwd|secret|authorization|cookie|api[-_]?key|session/i;

/**
 * A readable message for anything that can be thrown. A plain object (`throw { code: 'X' }`, a rejected promise
 * payload) would otherwise stringify as "[object Object]" and its content would be lost. Values under sensitive-looking
 * keys (tokens, passwords, secrets, auth headers, cookies) are redacted.
 */
function describeError(error: any): string {
  if (error?.message) {
    return error.message;
  }
  if (error && typeof error === 'object' && error.toString === Object.prototype.toString) {
    try {
      return JSON.stringify(error, (key, value) => (key && SENSITIVE_KEY.test(key) ? '[redacted]' : value)).slice(
        0,
        2000
      );
    } catch {
      return Object.prototype.toString.call(error);
    }
  }
  return String(error);
}
